import { useState } from 'react';

import { useNavigate } from '@tanstack/react-router';

import { integrationsApi, type Integration } from '@/api/queries/integrations';
import { segmentsApi, type Segment, type SegmentSyncRef } from '@/api/queries/workspace';
import { Button, Icon, IconButton } from '@/components/ui';
import { timeAgo } from '@/utils/formatDate';

import { CONTACT_SEARCH_FIELDS } from '@/routes/contacts/-components/list/ContactSearchFields';
import { conditionsToQuery } from '@/routes/contacts/-components/list/parseQuery';

import { ConnectSegmentModal } from './ConnectSegmentModal';
import { DesyncSegmentModal } from './DesyncSegmentModal';
import { SegmentBuilder } from './SegmentBuilder';
import { SegmentSyncBadge } from './SegmentSyncBadge';

export function SegmentsPage() {
  const { data: segments = [], isPending } = segmentsApi.useList();
  const { data: integrations = [] } = integrationsApi.useList();
  const deleteSegment = segmentsApi.useDelete();
  const [editing, setEditing] = useState<Segment | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  const exportAll = () => exportSegmentsCsv(segments);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <header className="page-header">
        <div>
          <h1 className="page-title">Segments</h1>
          <div className="page-subtitle">
            Dynamic audiences · auto-update as contact data changes
          </div>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            leftIcon={<Icon name="download" size={13} />}
            onClick={exportAll}
            disabled={segments.length === 0}
          >
            Export all
          </Button>
          <Button
            variant="primary"
            leftIcon={<Icon name="plus" size={13} />}
            onClick={() => setCreating(true)}
          >
            New segment
          </Button>
        </div>
      </header>

      {isPending ? (
        <div className="table-empty" style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
          Loading segments…
        </div>
      ) : segments.length === 0 ? (
        <EmptyState onCreate={() => setCreating(true)} />
      ) : (
        <div className="segment-grid">
          {segments.map((s) => (
            <SegmentCard
              key={s.id}
              segment={s}
              integrations={integrations}
              onEdit={() => setEditing(s)}
              onDelete={() => {
                if (confirm(`Delete segment "${s.name}"? This does not delete any contacts.`)) {
                  deleteSegment.mutate(s.id);
                }
              }}
            />
          ))}
        </div>
      )}

      <SegmentBuilder open={creating} onClose={() => setCreating(false)} />
      <SegmentBuilder open={!!editing} onClose={() => setEditing(undefined)} initial={editing} />
    </div>
  );
}

// Client-side CSV of the segment list — no server export endpoint exists for
// segments, and the list is small.
function exportSegmentsCsv(segments: Segment[]) {
  const header = ['name', 'description', 'contacts', 'rules', 'updatedAt'];
  const escape = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = segments.map((s) =>
    [
      s.name,
      s.description ?? '',
      s.contactCount ?? 0,
      s.conditions?.conditions?.length ?? 0,
      s.updatedAt ?? '',
    ]
      .map(escape)
      .join(','),
  );
  const csv = [header.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'segments.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function SegmentCard({
  segment,
  integrations,
  onEdit,
  onDelete,
}: {
  segment: Segment;
  integrations: Integration[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();
  const [connectOpen, setConnectOpen] = useState(false);
  const [desyncFor, setDesyncFor] = useState<string | null>(null);

  const isLive = (segment.conditions?.conditions?.length ?? 0) > 0;
  const links = Object.values(segment.integrationSyncRefs ?? {});

  return (
    <article className="segment-card">
      <div className="seg-head">
        <div className="seg-icon" style={{ background: segment.color }}>
          <Icon name="segments" size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="seg-name">
            {segment.name}
            {isLive ? (
              <span className="live-badge">Live</span>
            ) : (
              <span className="live-badge" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>
                Draft
              </span>
            )}
          </div>
          {segment.description && <div className="seg-desc">{segment.description}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="seg-count" style={{ color: segment.color }}>
            {(segment.contactCount ?? 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>contacts</div>
        </div>
      </div>
      {links.length > 0 && (
        <div className="seg-links">
          {links.map((ref) => (
            <LinkChip
              key={ref.integrationId}
              syncRef={ref}
              integration={integrations.find((i) => i.id === ref.integrationId)}
            />
          ))}
        </div>
      )}
      <div className="seg-foot">
        <span>
          {segment.conditions?.conditions?.length ?? 0} rule
          {(segment.conditions?.conditions?.length ?? 0) === 1 ? '' : 's'}
        </span>
        {segment.updatedAt && <span>· updated {timeAgo(segment.updatedAt)}</span>}
        <SegmentSyncBadge segmentId={segment.id} pollWhenIdle={links.some((l) => l.autoSync)} />
        <div className="seg-actions">
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Icon name="users" size={12} />}
            disabled={!isLive}
            title={isLive ? 'Open these contacts in the list' : 'Add a condition first'}
            onClick={() =>
              navigate({
                to: '/contacts',
                search: {
                  page: 0,
                  pageSize: 25,
                  q: conditionsToQuery(segment.conditions, CONTACT_SEARCH_FIELDS) || undefined,
                },
              })
            }
          >
            View contacts
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Icon name="link" size={12} />}
            onClick={() => setConnectOpen(true)}
          >
            Connect to
          </Button>
          <Button size="sm" variant="ghost" leftIcon={<Icon name="edit" size={12} />} onClick={onEdit}>
            Edit
          </Button>
          <IconButton
            icon="trash"
            iconSize={13}
            size="sm"
            label="Delete segment"
            variant="danger"
            onClick={onDelete}
          />
        </div>
      </div>
      <ConnectSegmentModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        segment={segment}
        onRemoveFromProvider={(integrationId) => {
          setConnectOpen(false);
          setDesyncFor(integrationId);
        }}
      />
      <DesyncSegmentModal
        open={desyncFor !== null}
        onClose={() => {
          setDesyncFor(null);
          setConnectOpen(true);
        }}
        segment={segment}
        integrationId={desyncFor ?? undefined}
      />
    </article>
  );
}

function LinkChip({
  syncRef,
  integration,
}: {
  syncRef: SegmentSyncRef;
  integration?: Integration;
}) {
  const name = integration?.name ?? 'Removed integration';
  const failed = syncRef.lastSyncStatus === 'failed';
  const problem = failed || !!syncRef.autoSyncPausedReason;
  const title = [
    name,
    syncRef.autoSync ? 'auto-sync on' : 'auto-sync off',
    syncRef.lastSyncAt ? `last sync ${timeAgo(syncRef.lastSyncAt)}` : 'not synced yet',
    syncRef.autoSyncPausedReason && `paused: ${syncRef.autoSyncPausedReason}`,
    failed && (syncRef.lastSyncError ? `failed: ${syncRef.lastSyncError}` : 'last sync failed'),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span className="ds-tag brand seg-link-chip" title={title}>
      {problem && <span className="seg-link-dot" aria-label="Sync problem" />}
      {name}
      {syncRef.autoSync && <span className="seg-link-auto">auto</span>}
    </span>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '40px 20px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--brand-soft)',
          color: 'var(--brand)',
          display: 'inline-grid',
          placeItems: 'center',
          marginBottom: 12,
        }}
      >
        <Icon name="segments" size={22} />
      </div>
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontWeight: 600,
          fontSize: 15,
          marginBottom: 4,
        }}
      >
        No segments yet
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 14 }}>
        Define a live audience with one or more conditions and a friendly name.
      </div>
      <Button variant="primary" leftIcon={<Icon name="plus" size={13} />} onClick={onCreate}>
        Create your first segment
      </Button>
    </div>
  );
}
